import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Mail, Lock, User, Eye, EyeOff, Sparkles,
    CheckCircle, Shield, FileCheck, ArrowLeft,
    RefreshCw, KeyRound,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Utility ────────────────────────────────────────────────────────────────────
const OTP_LENGTH = 6;

// ── Component ──────────────────────────────────────────────────────────────────
const AcceptInvite = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // ── Invite data (from /verify) ─────────────────────────────────────────────
    const [inviteData, setInviteData] = useState(null);

    // ── Step state machine ─────────────────────────────────────────────────────
    // "loading"  → verifying the invite token
    // "form"     → email / full_name / password form
    // "otp"      → OTP entry screen
    // "done"     → account created, countdown to login
    // "accepted" → already accepted
    // "dead"     → invite invalid / expired
    const [step, setStep] = useState("loading");

    // ── Form fields ────────────────────────────────────────────────────────────
    const [fullName, setFullName] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // ── OTP screen state ───────────────────────────────────────────────────────
    const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
    const [maskedEmail, setMaskedEmail] = useState("");
    const [verificationToken, setVerificationToken] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const otpRefs = useRef([]);

    // ── Shared UI state ────────────────────────────────────────────────────────
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState(null);
    const [countdown, setCountdown] = useState(3);

    const token = searchParams.get("token");

    // ── Verify invite token on mount ───────────────────────────────────────────
    useEffect(() => {
        if (!token) {
            setStep("dead");
            setFormError("Invalid invite link — no token provided.");
            return;
        }

        axios
            .post(`${API_URL}/api/public/invites/verify`, { token })
            .then((res) => {
                const data = res.data;
                if (data.code === "INVITE_ALREADY_ACCEPTED") {
                    setStep("accepted");
                    return;
                }
                if (!data.success) {
                    setFormError(data.message || "This invite is no longer valid.");
                    setStep("dead");
                    return;
                }
                setInviteData(data);
                setStep("form");
            })
            .catch((err) => {
                const code = err.response?.data?.code;
                if (code === "INVITE_ALREADY_ACCEPTED") {
                    setStep("accepted");
                } else {
                    setFormError(
                        err.response?.data?.detail ||
                        err.response?.data?.message ||
                        "Invite link is invalid or expired."
                    );
                    setStep("dead");
                }
            });
    }, [token]);

    // ── Countdown → login (done & accepted screens) ────────────────────────────
    useEffect(() => {
        if (step !== "done" && step !== "accepted") return;
        if (countdown <= 0) { navigate("/auth/login"); return; }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [step, countdown, navigate]);

    // ── Resend cooldown ticker ─────────────────────────────────────────────────
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    // ── STEP 1: "Continue" — request OTP ──────────────────────────────────────
    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setFormError(null);
        setBusy(true);
        try {
            const res = await axios.post(`${API_URL}/api/public/invites/send-otp`, {
                token,
            });
            setMaskedEmail(res.data.masked_email || "");
            setOtpDigits(Array(OTP_LENGTH).fill(""));
            setStep("otp");
            setResendCooldown(60);
            // Focus first OTP box on next tick
            setTimeout(() => otpRefs.current[0]?.focus(), 80);
        } catch (err) {
            setFormError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                "Failed to send verification code. Please try again."
            );
        } finally {
            setBusy(false);
        }
    };

    // ── STEP 2: Verify OTP ─────────────────────────────────────────────────────
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otp_code = otpDigits.join("");
        if (otp_code.length !== OTP_LENGTH) return;
        setFormError(null);
        setBusy(true);
        try {
            const res = await axios.post(`${API_URL}/api/public/invites/verify-otp`, {
                token,
                otp_code,
            });
            setVerificationToken(res.data.verification_token);
            // Auto-submit account creation immediately after OTP verified
            await _createAccount(res.data.verification_token);
        } catch (err) {
            setFormError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                "Invalid or expired code. Please try again."
            );
            setBusy(false);
        }
    };

    // ── STEP 3: Create account (called right after OTP verified) ───────────────
    const _createAccount = async (vToken) => {
        try {
            await axios.post(`${API_URL}/api/public/invites/accept`, {
                token,
                full_name: fullName,
                password,
                verification_token: vToken,
            });
            setCountdown(3);
            setStep("done");
        } catch (err) {
            setFormError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                "Account creation failed. Please try again."
            );
            setBusy(false);
        }
    };

    // ── Resend OTP ─────────────────────────────────────────────────────────────
    const handleResend = async () => {
        if (resendCooldown > 0 || busy) return;
        setFormError(null);
        setBusy(true);
        try {
            await axios.post(`${API_URL}/api/public/invites/resend-otp`, { token });
            setOtpDigits(Array(OTP_LENGTH).fill(""));
            setResendCooldown(60);
            setTimeout(() => otpRefs.current[0]?.focus(), 80);
        } catch (err) {
            setFormError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                "Could not resend code. Please try again."
            );
        } finally {
            setBusy(false);
        }
    };

    // ── OTP box key-handling ───────────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        const digit = value.replace(/\D/g, "").slice(-1);
        const next = [...otpDigits];
        next[index] = digit;
        setOtpDigits(next);
        if (digit && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasted) return;
        const next = Array(OTP_LENGTH).fill("");
        pasted.split("").forEach((d, i) => { next[i] = d; });
        setOtpDigits(next);
        const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
        otpRefs.current[focusIdx]?.focus();
    };

    // ── Shared right panel ─────────────────────────────────────────────────────
    const RightPanel = () => (
        <div className="hidden lg:flex w-[45%] bg-gradient-to-br from-[#033F99] to-[#033F99] p-8 items-center justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
            <div className="max-w-md relative z-10">
                <span className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-white/80 text-xs font-medium mb-4">
                    <Sparkles className="h-3 w-3" /> Business Operating System
                </span>
                <h2 className="text-2xl font-bold text-white mb-3">
                    Your business as one connected system
                </h2>
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                    Synexos is where your commercial decisions, operations,
                    finance, workforce, and capital come together — governed, auditable,
                    and intelligent.
                </p>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 mb-4">
                    <h3 className="text-white font-bold mb-3 text-sm flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" /> What Happens After Login
                    </h3>
                    <div className="space-y-2">
                        {[
                            "View what needs your attention today",
                            "Respond to approvals and tasks",
                            "Monitor risks and insights",
                            "Navigate into any solution",
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-white/80 text-xs">
                                <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
                                <span>{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    // ── Loading ────────────────────────────────────────────────────────────────
    if (step === "loading") return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
            <div className="text-center text-slate-500 text-sm animate-pulse">Verifying invite…</div>
        </div>
    );

    // ── Already accepted ───────────────────────────────────────────────────────
    if (step === "accepted") return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
            <div className="w-full max-w-sm text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-emerald-500" />
                    </div>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Already Accepted</h1>
                <p className="text-slate-500 text-sm mb-1">This invitation has already been accepted.</p>
                <p className="text-slate-400 text-xs mb-6">Your account is ready. Sign in to continue.</p>
                <button
                    onClick={() => navigate("/auth/login")}
                    className="w-full bg-[#033F99] hover:bg-blue-800 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                    Go to Login
                </button>
                <p className="mt-3 text-xs text-slate-400">Redirecting in {countdown}s…</p>
            </div>
        </div>
    );

    // ── Dead / invalid ─────────────────────────────────────────────────────────
    if (step === "dead") return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
            <div className="w-full max-w-sm text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                        <Mail className="w-8 h-8 text-red-400" />
                    </div>
                </div>
                <h1 className="text-xl font-bold text-slate-900 mb-2">Invite Link Issue</h1>
                <p className="text-red-500 text-sm mb-6">{formError}</p>
                <button onClick={() => navigate("/auth/login")} className="text-[#033F99] text-sm hover:underline">
                    Go to Login
                </button>
            </div>
        </div>
    );

    // ── Done (account created) ─────────────────────────────────────────────────
    if (step === "done") return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
            <div className="w-full max-w-sm text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-emerald-500" />
                    </div>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Account Created!</h1>
                <p className="text-slate-500 text-sm mb-6">
                    Your account is ready. You'll be redirected to login.
                </p>
                <button
                    onClick={() => navigate("/auth/login")}
                    className="w-full bg-[#033F99] hover:bg-blue-800 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                    Go to Login
                </button>
                <p className="mt-3 text-xs text-slate-400">Redirecting in {countdown}s…</p>
            </div>
        </div>
    );

    // ── Main layout (form + otp share left/right split) ────────────────────────
    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8FAFC]">
            {/* Left Side */}
            <div className="w-full lg:w-[55%] flex flex-col justify-center items-center px-4 sm:px-6 md:px-8 py-12 relative overflow-hidden">
                <div className="w-full max-w-md min-w-0 mx-auto">

                    {/* Logo */}
                    <div className="flex items-center gap-2 mb-6">
                        <img
                            src="/assets/synexos-logo.png"
                            alt="Synexos"
                            className="h-8 w-auto"
                            onError={(e) => (e.target.style.display = "none")}
                        />
                        <span className="text-lg font-bold text-slate-900">Synexos</span>
                    </div>

                    {/* ── FORM STEP ─────────────────────────────────────── */}
                    {step === "form" && (
                        <>
                            <h1 className="text-3xl font-bold text-slate-900 mb-1">Accept Invitation</h1>
                            <p className="text-slate-600 mb-6">Complete your account setup.</p>

                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                                <form onSubmit={handleRequestOtp} className="space-y-4">
                                    {/* Email — locked, sourced from invitation */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <input
                                                type="email"
                                                value={inviteData?.email || ""}
                                                disabled
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl bg-[#EBF2FF] text-slate-500 cursor-not-allowed"
                                            />
                                        </div>
                                    </div>

                                    {/* Full Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Full Name
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <input
                                                type="text"
                                                placeholder="Full Name"
                                                value={fullName}
                                                onChange={(e) => setFullName(e.target.value)}
                                                required
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:border-[#033F99] focus:ring-2 focus:ring-[#033F99]/20 outline-none text-slate-900 placeholder-slate-400"
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Create Password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                minLength={6}
                                                className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl focus:border-[#033F99] focus:ring-2 focus:ring-[#033F99]/20 outline-none text-slate-900"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {formError && (
                                        <p className="text-red-500 text-sm">{formError}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={busy || !fullName.trim() || !password}
                                        className="w-full bg-[#033F99] hover:bg-blue-800 disabled:opacity-50 text-white font-bold py-3 rounded-xl shadow-lg shadow-[#033F99]/30 transition-all flex items-center justify-center gap-2"
                                    >
                                        {busy ? "Sending code…" : "Continue"}
                                    </button>
                                </form>
                            </div>

                            {/* Security Badges */}
                            <div className="mt-4 flex justify-center gap-4 text-xs text-slate-500">
                                <div className="flex items-center gap-1"><Lock className="h-3 w-3 text-[#033F99]" /><span>Encrypted</span></div>
                                <div className="flex items-center gap-1"><Shield className="h-3 w-3 text-[#033F99]" /><span>Secure</span></div>
                                <div className="flex items-center gap-1"><FileCheck className="h-3 w-3 text-[#033F99]" /><span>Audited</span></div>
                            </div>
                        </>
                    )}

                    {/* ── OTP STEP ──────────────────────────────────────── */}
                    {step === "otp" && (
                        <>
                            <button
                                onClick={() => { setStep("form"); setFormError(null); }}
                                className="flex items-center gap-1 text-slate-500 text-sm hover:text-slate-700 mb-6 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" /> Back
                            </button>

                            <h1 className="text-3xl font-bold text-slate-900 mb-1">Verify Your Email</h1>
                            <p className="text-slate-600 mb-1">We've sent a verification code to:</p>
                            <p className="text-[#033F99] font-semibold mb-6">{maskedEmail}</p>

                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                                <div className="flex justify-center mb-5">
                                    <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                                        <KeyRound className="w-7 h-7 text-[#033F99]" />
                                    </div>
                                </div>

                                <form onSubmit={handleVerifyOtp} className="space-y-5">
                                    {/* 6-box OTP input */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-3 text-center">
                                            Enter 6-digit code
                                        </label>
                                        <div
                                            className="flex justify-center gap-2"
                                            onPaste={handleOtpPaste}
                                        >
                                            {otpDigits.map((digit, idx) => (
                                                <input
                                                    key={idx}
                                                    ref={(el) => (otpRefs.current[idx] = el)}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                                    className="w-11 h-13 text-center text-xl font-bold border-2 border-slate-300 rounded-xl
                                                               focus:border-[#033F99] focus:ring-2 focus:ring-[#033F99]/20 outline-none
                                                               text-slate-900 transition-colors"
                                                    style={{ height: "3.25rem" }}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {formError && (
                                        <p className="text-red-500 text-sm text-center">{formError}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={busy || otpDigits.join("").length !== OTP_LENGTH}
                                        className="w-full bg-[#033F99] hover:bg-blue-800 disabled:opacity-50 text-white font-bold py-3 rounded-xl shadow-lg shadow-[#033F99]/30 transition-all"
                                    >
                                        {busy ? "Verifying…" : "Verify & Create Account"}
                                    </button>

                                    {/* Resend */}
                                    <div className="text-center">
                                        {resendCooldown > 0 ? (
                                            <p className="text-slate-400 text-sm">
                                                Resend available in {resendCooldown}s
                                            </p>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleResend}
                                                disabled={busy}
                                                className="flex items-center gap-1 text-[#033F99] text-sm hover:underline mx-auto disabled:opacity-50"
                                            >
                                                <RefreshCw className="h-3.5 w-3.5" /> Resend code
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Security Badges */}
                            <div className="mt-4 flex justify-center gap-4 text-xs text-slate-500">
                                <div className="flex items-center gap-1"><Lock className="h-3 w-3 text-[#033F99]" /><span>Encrypted</span></div>
                                <div className="flex items-center gap-1"><Shield className="h-3 w-3 text-[#033F99]" /><span>Secure</span></div>
                                <div className="flex items-center gap-1"><FileCheck className="h-3 w-3 text-[#033F99]" /><span>Audited</span></div>
                            </div>
                        </>
                    )}

                </div>
            </div>

            <RightPanel />
        </div>
    );
};

export default AcceptInvite;
